import { API } from '../config';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Shield, X, KeyRound, Clock, Search } from 'lucide-react';
import { toast } from 'sonner';


export default function HiddenPanel() {
  const { getAuthHeaders, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [akisToken, setAkisToken] = useState(null);
  const [ttlMinutes, setTtlMinutes] = useState(15);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);

  useEffect(() => { if (!isAdmin) navigate('/'); }, [isAdmin, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const checkActivity = () => {
      const remaining = ttlMinutes * 60 - (Date.now() - lastActivityRef.current) / 1000;
      setTimeRemaining(Math.max(0, Math.floor(remaining)));
      if (remaining <= 0) { handleExit(); toast.error('Oturum süresi doldu'); }
    };
    timerRef.current = setInterval(checkActivity, 1000);
    const resetActivity = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener('mousemove', resetActivity);
    window.addEventListener('keydown', resetActivity);
    return () => { if (timerRef.current) clearInterval(timerRef.current); window.removeEventListener('mousemove', resetActivity); window.removeEventListener('keydown', resetActivity); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, ttlMinutes]);

  const handlePinSubmit = async () => {
    if (pin.length !== 4) return;
    try {
      const res = await axios.post(`${API}/admin/akis/auth`, { pin }, { headers: getAuthHeaders() });
      setAkisToken(res.data.akis_token); setTtlMinutes(res.data.ttl_minutes || 15);
      setIsAuthenticated(true); setPinError(''); lastActivityRef.current = Date.now();
      fetchUsers(res.data.akis_token);
    } catch (err) { setPinError(err.response?.data?.detail || 'Geçersiz PIN'); setPin(''); }
  };

  useEffect(() => { if (pin.length === 4) handlePinSubmit(); }, [pin]); // eslint-disable-line

  const fetchUsers = async (token) => { try { const res = await axios.get(`${API}/admin/akis/users`, { headers: { Authorization: `Bearer ${token || akisToken}` } }); setUsers(res.data); } catch { toast.error('Kullanıcılar yüklenemedi'); } };
  const fetchMessages = async (userId, platform, kw) => {
    setLoading(true);
    try { let url = `${API}/admin/akis/messages?user_id=${userId}`; if (platform) url += `&platform=${platform}`; if (kw) url += `&keyword=${encodeURIComponent(kw)}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${akisToken}` } }); setMessages(res.data); setSelectedUser(userId); setSelectedPlatform(platform || '');
    } catch (err) { if (err.response?.status === 403) { handleExit(); toast.error('AKIS oturumu sona erdi. Tekrar kimlik doğrulayın.'); } else { toast.error('Mesajlar yüklenemedi'); } }
    finally { setLoading(false); }
  };

  const handleExit = () => { setIsAuthenticated(false); setAkisToken(null); setPin(''); setMessages([]); setUsers([]); setSelectedUser(null); setKeyword(''); if (timerRef.current) clearInterval(timerRef.current); navigate('/admin'); };
  const formatTime = (ts) => ts ? new Date(ts).toLocaleString('tr-TR') : '-';
  const formatRemaining = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (!isAuthenticated) {
    return (
      <div className="min-h-dvh bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center space-y-6">
          <div className="flex justify-center"><div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center"><KeyRound className="w-8 h-8 text-muted-foreground" /></div></div>
          <div><h1 className="text-lg font-semibold font-display">Devam etmek için PIN girin</h1><p className="text-sm text-muted-foreground mt-1">İkinci kimlik doğrulama gerekli</p></div>
          <div className="flex justify-center">
            <InputOTP data-testid="akis-pin-input" maxLength={4} value={pin} onChange={setPin}>
              <InputOTPGroup><InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} /><InputOTPSlot index={3} /></InputOTPGroup>
            </InputOTP>
          </div>
          {pinError && <p className="text-sm text-destructive">{pinError}</p>}
          <Button data-testid="akis-unlock-button" onClick={handlePinSubmit} disabled={pin.length !== 4} className="rounded-xl">Doğrula</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <div className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border/70 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-muted-foreground" /><span className="text-sm font-medium text-muted-foreground">Güvenli Mesaj Görüntüleyici</span></div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="w-3 h-3" /><span className={timeRemaining < 120 ? 'text-destructive font-medium' : ''}>{formatRemaining(timeRemaining)}</span></div>
          <Button data-testid="akis-exit-button" variant="ghost" size="sm" className="rounded-xl text-xs" onClick={handleExit}><X className="w-4 h-4 mr-1" /> Çıkış</Button>
        </div>
      </div>
      <div className="flex-1 flex flex-col lg:flex-row">
        <div className="w-full lg:w-72 border-b lg:border-b-0 lg:border-r border-border/70">
          <div className="px-4 py-3 border-b border-border/50"><h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Kullanıcılar</h3></div>
          <ScrollArea className="max-h-48 lg:max-h-[calc(100vh-120px)]">
            {users.map(u => (<button key={u.id} onClick={() => fetchMessages(u.id)} className={`w-full text-left px-4 py-3 text-sm hover:bg-secondary/50 transition-colors border-b border-border/30 ${selectedUser === u.id ? 'bg-secondary/70' : ''}`}><p className="font-medium">{u.name || u.email}</p><p className="text-xs text-muted-foreground">{u.email}</p></button>))}
          </ScrollArea>
        </div>
        <div className="flex-1 flex flex-col">
          {selectedUser ? (<>
            <div className="px-4 py-3 border-b border-border/50 flex items-center gap-2 flex-wrap">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Mesajlar</h3>
              <div className="flex gap-1">{['', 'telegram', 'whatsapp'].map(p => (<Button key={p} variant={selectedPlatform === p ? 'default' : 'outline'} size="sm" className="h-6 text-[10px] rounded-lg px-2" onClick={() => fetchMessages(selectedUser, p, keyword)}>{p || 'Tümü'}</Button>))}</div>
              <div className="relative flex-1 min-w-[150px]"><Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" /><input className="w-full pl-7 pr-2 py-1 rounded-lg bg-background border border-border/70 text-[11px]" placeholder="Anahtar kelimeye göre filtrele..." value={keyword} onChange={e => setKeyword(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchMessages(selectedUser, selectedPlatform, keyword)} /></div>
            </div>
            <ScrollArea className="flex-1"><Table data-testid="akis-message-table"><TableHeader><TableRow><TableHead className="text-xs">Zaman</TableHead><TableHead className="text-xs">Platform</TableHead><TableHead className="text-xs">Yön</TableHead><TableHead className="text-xs">Gönderen</TableHead><TableHead className="text-xs">Mesaj</TableHead></TableRow></TableHeader><TableBody>
              {messages.map(m => (<TableRow key={m.id}><TableCell className="text-xs text-muted-foreground whitespace-nowrap">{formatTime(m.timestamp)}</TableCell><TableCell><Badge variant="outline" className="text-[10px]">{m.platform}</Badge></TableCell><TableCell><Badge variant={m.direction === 'in' ? 'secondary' : 'default'} className="text-[10px]">{m.direction === 'in' ? 'Gelen' : 'Giden'}</Badge></TableCell><TableCell className="text-xs">{m.sender?.display_name || '-'}</TableCell><TableCell className="text-xs max-w-[300px] truncate">{m.text}</TableCell></TableRow>))}
            </TableBody></Table>
              {messages.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">{loading ? 'Yükleniyor...' : 'Mesaj bulunamadı'}</div>}
            </ScrollArea>
          </>) : (<div className="flex-1 flex items-center justify-center"><p className="text-muted-foreground text-sm">Mesajları görüntülemek için bir kullanıcı seçin</p></div>)}
        </div>
      </div>
    </div>
  );
}
